// askSmart-ui/src/pages/TOSP/components/CamCategoryModal.tsx
import React, { useEffect, useState } from "react";
import SplitMappingFields, { EntityOption } from "./SplitMappingFields";
import {
  CamCategory,
  emptySplitMapping,
  SplitAmong,
  SplitMapping,
  splitPercentError,
  newId,
} from "../types";
import { requiredText, sameText } from "../validation";

interface CamCategoryModalProps {
  isOpen: boolean;
  /** The category being edited, or null when adding. */
  category: CamCategory | null;
  /** Every category in the draft, for the unique-name check. */
  categories: CamCategory[];
  entityOptions: (splitAmong: SplitAmong | "") => EntityOption[];
  onClose: () => void;
  onSave: (category: CamCategory) => void;
}

// Add / edit a CAM category and the split that goes with it — one modal for
// both, so the name and its division are always defined together.
export default function CamCategoryModal({
  isOpen,
  category,
  categories,
  entityOptions,
  onClose,
  onSave,
}: CamCategoryModalProps) {
  const [name, setName] = useState("");
  const [split, setSplit] = useState<SplitMapping>(emptySplitMapping());
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(category?.name || "");
    setSplit(category?.split ? { ...category.split } : emptySplitMapping());
    setError(null);
    setHelpOpen(false);
  }, [isOpen, category]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nameError = requiredText(name, "Category");
    if (nameError) {
      setError(nameError);
      return;
    }
    if (categories.some((c) => c.id !== category?.id && sameText(c.name, name))) {
      setError(`"${name.trim()}" is already a category.`);
      return;
    }
    // A percentage split that doesn't add up isn't a split — hold the save.
    const percentError = splitPercentError(split);
    if (percentError) {
      setError(percentError);
      return;
    }
    onSave({ id: category?.id || newId("cam"), name: name.trim(), split });
  };

  return (
    <div className="smrtdocs-modal-overlay">
      {/* Narrow and stacked: a category is one short name plus a few choices,
          so a wide three-column form gave it far more room than it needs. */}
      <div className="smrtdocs-modal tosp-modal-narrow">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {category ? "Edit CAM Category" : "Add CAM Category"}
          </h2>
        </div>
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          <div className="tosp-stack">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-cam-name">
                Category <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-cam-name"
                className="manage-input"
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <div className="tosp-section">
            {/* Behind "?" like the configuration panels — the explanation is
                worth having, but not worth the space every time. */}
            <div className="tosp-section-header">
              <span className="tosp-section-title">Split</span>
              <button
                type="button"
                className="tosp-help-toggle"
                aria-expanded={helpOpen}
                title="What's this?"
                onClick={() => setHelpOpen((open) => !open)}
              >
                ?
              </button>
            </div>
            {helpOpen && (
              <p className="tosp-field-hint" style={{ marginBottom: 12 }}>
                How this category's costs are divided. A budget year copies this when it's set
                up, so changing it later only affects budgets built from then on.
              </p>
            )}

            <div className="tosp-stack">
              {/* Header and exempt rows still need a category so the budget
                  reads properly, but they aren't divided by anything. */}
              <div className="tosp-field">
                <span className="tosp-label">Treatment</span>
                <div className="tosp-radio-group" role="radiogroup" aria-label="Treatment">
                  <label className="tosp-radio">
                    <input
                      type="radio"
                      name="tosp-cam-treatment"
                      checked={!split.ignored}
                      onChange={() => setSplit((prev) => ({ ...prev, ignored: false }))}
                    />
                    <span>Split</span>
                  </label>
                  <label className="tosp-radio">
                    <input
                      type="radio"
                      name="tosp-cam-treatment"
                      checked={split.ignored}
                      onChange={() => setSplit((prev) => ({ ...prev, ignored: true }))}
                    />
                    <span>Ignore</span>
                  </label>
                </div>
              </div>

              {!split.ignored && (
                <SplitMappingFields
                  value={split}
                  idPrefix="tosp-cam-split"
                  entityOptions={entityOptions}
                  onChange={(patch) => setSplit((prev) => ({ ...prev, ...patch }))}
                />
              )}
            </div>
          </div>

          {error && <p className="tosp-field-error">{error}</p>}

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
